import React, { PureComponent } from "react"
import classNames from "classnames"
import Link from "next/link"
import { Mutation } from "react-apollo"

import Cta from "../cta/cta"
import { CURRENT_USER_QUERY } from "../me/me"
import {
  SAVE_FOUNDER_QUESTIONNAIRE,
  GENERATE_FOUNDER_ASSIST_INITIAL,
  MY_FOUNDER_ASSIST_QUERY,
} from "../../lib/founder-assist-queries"
import { GenerationWatcher, graphQLErrorText } from "../../lib/founder-assist-jobs"

import "./founder-questionnaire.scss"

// Phase 4 unification: the questionnaire is now a view over the User profile.
// Each field is one of two sources:
//
//   - "derived": the value comes from existing User columns the rest of
//     FounderCloud already maintains (companyName, shortDescription,
//     elevatorPitchForStartup, whyMakeGoodCofounder, hasSkills, etc.).
//     These render read-only with an "Edit on your profile" affordance —
//     no duplicate data entry, and changes made elsewhere flow here.
//
//   - "user": net-new strategic fields lifted onto the User row (Phase 4a).
//     These render as editable textareas; saves go through
//     SAVE_FOUNDER_QUESTIONNAIRE which routes to updateUser server-side.
//
// The OLD FounderAssist questionnaire columns aren't deleted — they're just
// not read by the new resolvers. Backfill script copies them to User; this
// component never touches them directly.

function joinSentences(parts) {
  return parts
    .filter((p) => p && String(p).trim())
    .map((p) => String(p).trim().replace(/\.$/, ""))
    .join(". ")
}

function deriveCompanyBackground(me) {
  if (!me) return ""
  const parts = [me.companyName, me.shortDescription, me.longDescription]
  const joined = joinSentences(parts)
  if (joined) return joined + "."
  // For cofounders who haven't set company-side fields, fall back to the
  // elevator pitch — they almost certainly wrote one at signup.
  return me.elevatorPitchForStartup || ""
}

function deriveCompanyGoals(me) {
  if (!me) return ""
  return me.elevatorPitchForStartup || ""
}

function deriveCompanyPeopleAndSkills(me) {
  if (!me) return ""
  const skills = (me.hasSkills || []).map((s) => s.label).filter(Boolean)
  const hours = me.hoursPerWeekAvailable && me.hoursPerWeekAvailable.label
  if (!skills.length && !hours) return ""
  const bits = []
  if (skills.length) bits.push(`Skills on hand: ${skills.join(", ")}`)
  if (hours) bits.push(`Available ${hours}`)
  return bits.join(". ") + "."
}

function derivePersonalBackground(me) {
  if (!me) return ""
  return me.whyMakeGoodCofounder || ""
}

function derivePersonalSkills(me) {
  if (!me) return ""
  const skills = (me.hasSkills || []).map((s) => s.label).filter(Boolean)
  return skills.join(", ")
}

const COMPANY_SECTIONS = [
  {
    title: "About the company",
    fields: [
      {
        key: "companyBackground",
        label: "Background",
        source: "derived",
        derive: deriveCompanyBackground,
        emptyHint:
          "Add a company name and a one-line description on your profile so we can use them here.",
        editLink: "/profile",
        editLinkLabel: "Edit on your profile",
      },
      {
        key: "companyGoals",
        label: "Goals",
        source: "derived",
        derive: deriveCompanyGoals,
        emptyHint:
          "Add an elevator pitch on your profile so we know what success looks like.",
        editLink: "/profile",
        editLinkLabel: "Edit on your profile",
      },
    ],
  },
  {
    title: "What you have now",
    fields: [
      {
        key: "companyCurrentResources",
        label: "Current resources",
        source: "user",
        hint:
          "Money, tools, distribution, customers — what you already have.",
      },
      {
        key: "peopleAndSkills",
        label: "People & skills",
        source: "derived",
        derive: deriveCompanyPeopleAndSkills,
        emptyHint:
          "Add your skills and weekly availability on your profile.",
        editLink: "/profile",
        editLinkLabel: "Edit on your profile",
      },
    ],
  },
  {
    title: "Where you're going",
    fields: [
      {
        key: "companyUniqueValueIp",
        label: "Unique value / IP",
        source: "user",
        hint: "What's hard to copy about your edge?",
      },
      {
        key: "companyFutureExpansion",
        label: "Future expansion ideas",
        source: "user",
        hint: "Adjacent products, markets, channels worth considering.",
      },
      {
        key: "companyCashFunding",
        label: "Cash / funding",
        source: "user",
        hint:
          "Runway, revenue, fundraising plans — what you have and what you need.",
      },
      {
        key: "companyOtherNotes",
        label: "Other notes",
        source: "user",
      },
    ],
  },
]

const INDIVIDUAL_SECTIONS = [
  {
    title: "About you",
    fields: [
      {
        key: "personalBackground",
        label: "Background",
        source: "derived",
        derive: derivePersonalBackground,
        emptyHint:
          "Add a 'why I'd make a good cofounder' note on your profile.",
        editLink: "/profile",
        editLinkLabel: "Edit on your profile",
      },
      {
        key: "personalGoals",
        label: "Personal goals",
        source: "user",
        hint: "What do you want the next 12 months to make possible?",
      },
    ],
  },
  {
    title: "Strengths & interests",
    fields: [
      {
        key: "personalInterests",
        label: "Interests",
        source: "user",
        hint: "Domains and problems that pull you in.",
      },
      {
        key: "skills",
        label: "Skills",
        source: "derived",
        derive: derivePersonalSkills,
        emptyHint:
          "Pick a couple of skills on your profile so we know your strengths.",
        editLink: "/profile",
        editLinkLabel: "Edit on your profile",
      },
      {
        key: "individualOtherNotes",
        label: "Other notes",
        source: "user",
      },
    ],
  },
]

// Editable-field keys — used for progress + initial-value extraction.
const COMPANY_USER_FIELDS = [
  "companyCurrentResources",
  "companyUniqueValueIp",
  "companyFutureExpansion",
  "companyCashFunding",
  "companyOtherNotes",
]
const INDIVIDUAL_USER_FIELDS = [
  "personalGoals",
  "personalInterests",
  "individualOtherNotes",
]

// The minimum context the AI needs to draft a useful first plan. Much more
// lenient than before because most users now meet it the moment they finish
// signup — the derived fields fill themselves in from signup data.
export function hasMinimumQuestionnaireFromMe(me, mode) {
  if (!me) return false
  if (mode === "individual") {
    const background = derivePersonalBackground(me).trim()
    const skills = derivePersonalSkills(me).trim()
    const goals = (me.personalGoals || "").trim()
    return Boolean(background && (skills || goals))
  }
  const background = deriveCompanyBackground(me).trim()
  const goals = deriveCompanyGoals(me).trim()
  const teamHint = deriveCompanyPeopleAndSkills(me).trim()
  const anyStrategic = COMPANY_USER_FIELDS.some(
    (k) => me[k] && me[k].trim(),
  )
  return Boolean((background || goals) && (teamHint || anyStrategic))
}

// Kept for backward-compat callers that still pass fa-shaped values. New
// callers should use hasMinimumQuestionnaireFromMe.
export function hasMinimumQuestionnaire(values, mode) {
  return hasMinimumQuestionnaireFromMe(values, mode)
}

function sectionFillCount(section, me) {
  let filled = 0
  for (const f of section.fields) {
    if (f.source === "derived") {
      if (f.derive(me)) filled++
    } else {
      if (me && me[f.key] && String(me[f.key]).trim()) filled++
    }
  }
  return { filled, total: section.fields.length }
}

class FounderQuestionnaire extends PureComponent {
  constructor(props) {
    super(props)
    this.state = {
      values: this.extractUserValues(props.me),
      openSections: [0, 1, 2],
      savingKey: null,
      savedKey: null,
      generating: false,
      generateError: null,
    }
    // key -> { timer, save } for debounced saves that haven't fired yet.
    this._pendingSaves = {}
    this._generation = new GenerationWatcher((errorText) => {
      this.safeSetState({ generating: false, generateError: errorText })
      if (errorText) {
        if (this.props.onGenerateError) this.props.onGenerateError(errorText)
      } else if (this.props.onGenerateComplete) {
        this.props.onGenerateComplete()
      }
    })
  }

  // Pull only the editable User-strategic fields into local state for
  // controlled-input behavior. Derived fields are computed at render time.
  extractUserValues(me) {
    const m = me || {}
    return {
      companyCurrentResources: m.companyCurrentResources || "",
      companyUniqueValueIp: m.companyUniqueValueIp || "",
      companyFutureExpansion: m.companyFutureExpansion || "",
      companyCashFunding: m.companyCashFunding || "",
      companyOtherNotes: m.companyOtherNotes || "",
      personalGoals: m.personalGoals || "",
      personalInterests: m.personalInterests || "",
      individualOtherNotes: m.individualOtherNotes || "",
    }
  }

  handleGenerate = (generateMutation) => {
    this.safeSetState({ generating: true, generateError: null })
    if (this.props.onGenerateStart) this.props.onGenerateStart()
    // The mutation only starts the job; the watcher finishes when the polled
    // fa shows generationInProgress back to false.
    this._generation.start()
    generateMutation()
      .then(() => this._generation.update(this.props.fa))
      .catch((e) => {
        this._generation.stop()
        const msg = graphQLErrorText(
          e,
          "We had trouble drafting your plan. Try again, or simplify one of your answers slightly."
        )
        this.safeSetState({ generating: false, generateError: msg })
        if (this.props.onGenerateError) this.props.onGenerateError(msg)
      })
  }

  componentDidMount() {
    this._mounted = true
  }

  componentWillUnmount() {
    this._mounted = false
    // Send debounced saves now instead of dropping them. The questionnaire
    // unmounts when "Refine" collapses or the page moves on after a
    // generate, often inside the debounce window after a blur. The mutation
    // still completes (and refetches) after unmount.
    Object.keys(this._pendingSaves).forEach((key) => this.runSave(key))
    if (this._savedFlashTimer) clearTimeout(this._savedFlashTimer)
    this._generation.stop()
  }

  safeSetState(state) {
    if (this._mounted) this.setState(state)
  }

  componentDidUpdate(prevProps) {
    // When the user's me row changes (e.g., after a profile edit elsewhere),
    // re-pull the editable values so they stay in sync.
    if (this.props.me && prevProps.me && this.props.me.id !== prevProps.me.id) {
      this.setState({ values: this.extractUserValues(this.props.me) })
    } else if (this.props.me && !prevProps.me) {
      this.setState({ values: this.extractUserValues(this.props.me) })
    }
    this._generation.update(this.props.fa)
  }

  toggleSection = (idx) => {
    this.setState((p) => ({
      openSections: p.openSections.includes(idx)
        ? p.openSections.filter((i) => i !== idx)
        : p.openSections.concat(idx),
    }))
  }

  onFieldChange = (key, value) => {
    this.setState((p) => ({ values: { ...p.values, [key]: value } }))
  }

  triggerSave = (key, save) => {
    const pending = this._pendingSaves[key]
    if (pending) clearTimeout(pending.timer)
    this._pendingSaves[key] = {
      save,
      timer: setTimeout(() => this.runSave(key), 700),
    }
  }

  runSave(key) {
    const pending = this._pendingSaves[key]
    if (!pending) return
    clearTimeout(pending.timer)
    delete this._pendingSaves[key]

    this.safeSetState({ savingKey: key, savedKey: null })
    const mode = (this.props.fa && this.props.fa.mode) || "company"
    pending
      .save({
        variables: {
          mode,
          fields: { [key]: this.state.values[key] || "" },
        },
      })
      .then(() => {
        this.safeSetState({ savingKey: null, savedKey: key })
        if (this._savedFlashTimer) clearTimeout(this._savedFlashTimer)
        this._savedFlashTimer = setTimeout(() => {
          this.safeSetState((p) =>
            p.savedKey === key ? { savedKey: null } : null,
          )
        }, 1500)
      })
      .catch(() => {
        this.safeSetState({ savingKey: null })
      })
  }

  renderField(field, me, save) {
    const { values, savingKey, savedKey } = this.state

    if (field.source === "derived") {
      const value = field.derive(me)
      const hasValue = Boolean(value && String(value).trim())
      return (
        <div
          className={classNames(
            "founder-questionnaire__field",
            "founder-questionnaire__field--derived",
            { "founder-questionnaire__field--empty": !hasValue }
          )}
          key={field.key}
        >
          <div className="founder-questionnaire__label">
            <span>{field.label}</span>
            <span
              className="founder-questionnaire__derived-tag"
              title="Pulled from your FounderCloud profile so you don't have to re-enter it here."
            >
              From your profile
            </span>
          </div>
          <div className="founder-questionnaire__derived-body">
            {hasValue ? (
              <p className="founder-questionnaire__derived-value">{value}</p>
            ) : (
              <p className="founder-questionnaire__derived-empty">
                {field.emptyHint}
              </p>
            )}
            {field.editLink && (
              <Link href={field.editLink}>
                <a className="founder-questionnaire__derived-link">
                  {field.editLinkLabel || "Edit on your profile"} →
                </a>
              </Link>
            )}
          </div>
        </div>
      )
    }

    return (
      <label key={field.key} className="founder-questionnaire__field">
        <span className="founder-questionnaire__label">
          {field.label}
          {field.required && (
            <span
              className="founder-questionnaire__required"
              title="Required"
            >
              *
            </span>
          )}
          <span
            className="founder-questionnaire__status"
            aria-live="polite"
          >
            {savingKey === field.key && "Saving…"}
            {savedKey === field.key && "Saved"}
          </span>
        </span>
        {field.hint && (
          <span className="founder-questionnaire__hint">{field.hint}</span>
        )}
        <textarea
          value={values[field.key] || ""}
          onChange={(e) => this.onFieldChange(field.key, e.target.value)}
          onBlur={() => this.triggerSave(field.key, save)}
          placeholder={field.hint || ""}
          rows={4}
          maxLength={4000}
          className="founder-questionnaire__textarea"
        />
      </label>
    )
  }

  render() {
    const { fa, me, showGenerateCta } = this.props
    const mode = (fa && fa.mode) || "company"
    const sections = mode === "individual" ? INDIVIDUAL_SECTIONS : COMPANY_SECTIONS
    const { openSections, generateError } = this.state
    // generationInProgress also covers a reload mid-generation.
    const generating =
      this.state.generating || Boolean(fa && fa.generationInProgress)

    // Progress counts derived + editable fields equally — a derived field
    // that's filled in (from signup) is just as much "you've answered this"
    // as one the user typed themselves.
    const totalFields = sections.reduce((s, sec) => s + sec.fields.length, 0)
    const filledFields = sections.reduce(
      (s, sec) => s + sectionFillCount(sec, me).filled,
      0,
    )
    const progressPct =
      totalFields > 0 ? Math.round((filledFields / totalFields) * 100) : 0
    const meetsMin = hasMinimumQuestionnaireFromMe(me, mode)

    return (
      <Mutation
        mutation={SAVE_FOUNDER_QUESTIONNAIRE}
        refetchQueries={[
          { query: MY_FOUNDER_ASSIST_QUERY },
          { query: CURRENT_USER_QUERY },
        ]}
      >
        {(save) => (
          <div className="founder-questionnaire">
            {showGenerateCta && (
              <header className="founder-questionnaire__progress">
                <div className="founder-questionnaire__progress-row">
                  <span className="founder-questionnaire__progress-label">
                    {filledFields} of {totalFields} answered
                  </span>
                  <span
                    className={classNames(
                      "founder-questionnaire__progress-status",
                      {
                        "founder-questionnaire__progress-status--ready":
                          meetsMin,
                      }
                    )}
                  >
                    {meetsMin
                      ? "Ready to generate"
                      : "Add a bit more on your profile or below"}
                  </span>
                </div>
                <div
                  className="founder-questionnaire__progress-bar"
                  role="progressbar"
                  aria-valuenow={progressPct}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <span
                    className="founder-questionnaire__progress-fill"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
              </header>
            )}
            {sections.map((section, idx) => {
              const { filled, total } = sectionFillCount(section, me)
              const isOpen = openSections.includes(idx)
              return (
                <section
                  key={idx}
                  className={classNames("founder-questionnaire__section", {
                    "founder-questionnaire__section--open": isOpen,
                  })}
                >
                  <button
                    type="button"
                    className="founder-questionnaire__heading"
                    onClick={() => this.toggleSection(idx)}
                    aria-expanded={isOpen}
                  >
                    <span className="founder-questionnaire__heading-title">
                      {section.title}
                    </span>
                    <span className="founder-questionnaire__heading-count">
                      {filled} / {total} filled
                    </span>
                    <span
                      className={classNames("founder-questionnaire__chevron", {
                        "founder-questionnaire__chevron--open": isOpen,
                      })}
                      aria-hidden
                    />
                  </button>
                  {isOpen && (
                    <div className="founder-questionnaire__fields">
                      {section.fields.map((field) =>
                        this.renderField(field, me, save),
                      )}
                    </div>
                  )}
                </section>
              )
            })}

            {showGenerateCta && (
              <Mutation
                mutation={GENERATE_FOUNDER_ASSIST_INITIAL}
                refetchQueries={[
                  { query: MY_FOUNDER_ASSIST_QUERY },
                  { query: CURRENT_USER_QUERY },
                ]}
              >
                {(generateMutation) => (
                  <div
                    className={classNames(
                      "founder-questionnaire__cta-bar",
                      {
                        "founder-questionnaire__cta-bar--generating":
                          generating,
                        "founder-questionnaire__cta-bar--ready": meetsMin,
                      }
                    )}
                  >
                    <div className="founder-questionnaire__cta-bar-inner">
                      <div className="founder-questionnaire__cta-bar-text">
                        <strong>
                          {generating
                            ? "Drafting your plan…"
                            : meetsMin
                              ? "You're ready to go"
                              : "Almost there"}
                        </strong>
                        <span>
                          {generating
                            ? "This usually takes ~30 seconds. You can keep editing while we draft."
                            : meetsMin
                              ? "We have enough to draft your summary, plan, and first actions."
                              : "Fill any one strategic field above, or add a description on your profile."}
                        </span>
                      </div>
                      <Cta
                        tag="button"
                        type="button"
                        text={
                          generating
                            ? "Drafting…"
                            : "Generate my plan →"
                        }
                        disabled={!meetsMin || generating}
                        loading={generating}
                        onClick={() => this.handleGenerate(generateMutation)}
                      />
                    </div>
                    {generating && (
                      <div
                        className="founder-questionnaire__cta-bar-progress"
                        aria-hidden
                      >
                        <span />
                      </div>
                    )}
                    {generateError && !generating && (
                      <div
                        className="founder-questionnaire__cta-bar-error"
                        role="alert"
                      >
                        {generateError}
                      </div>
                    )}
                  </div>
                )}
              </Mutation>
            )}
          </div>
        )}
      </Mutation>
    )
  }
}

export default FounderQuestionnaire
